import { ValidatorFn } from '@angular/forms';
import {
    OnOpenEventHandler,
    BaseChangeEvent,
    FieldOptions,
    AutocompleteRequestFilterCriterias,
} from './fieldDefinitionOptions';
import { FieldOption } from './fieldPropertyTypes';
import { DateRangeType } from './field-types';
import { signal, Signal } from '@angular/core';

export class FieldBase<T> {
    value: T | undefined = undefined;
    key = '';
    label = '';
    placeholder = '';
    alwaysShowPlaceholder = false;
    visible = true;
    order = 1;
    readonly = false;
    controlType = '';
    type = '';
    options: FieldOption[] = [];
    internalFields: FieldBase<T>[] = [];
    className = 'field';
    labelClassName = '';
    icon = '';
    variant?: string = '';
    disabled?: boolean = false;
    validators?: ValidatorFn[] = [];
    hint?: string = '';
    url?: string = '';
    isUpdating?: Signal<boolean> = signal<boolean>(false);
    suffix?: string = '';
    fieldName?: string = '';
    onOpen?: (onOpenSuccessHandler: OnOpenEventHandler) => void = undefined;
    onChange?: (event: BaseChangeEvent) => void = () => {};
    onBlur?: (event: FocusEvent) => void = () => {};
    onSelect?: (selectedItem: string | FieldOption, key: string) => void = () => {};
    requestMapperFn?: (searchTerm: string) => AutocompleteRequestFilterCriterias[];
    responseMapperFn?: (response: any) => FieldOption[] = () => [];
    shouldHaveLazyLoad?: boolean = false;

    constructor(options: FieldOptions<T> = {}) {
        Object.assign(this, options);
    }
}

export class Section extends FieldBase<unknown> {
    constructor(options: FieldOptions<unknown>) {
        const expandedOptions = {
            ...options,
            controlType: 'section',
            className: options.className || 'w-full',
        };

        super(expandedOptions);
    }
}

export class ConditionalSection extends FieldBase<unknown> {
    constructor(options: FieldOptions<unknown>) {
        const expandedOptions = {
            ...options,
            controlType: 'conditionalSection',
            className: options.className || 'w-full',
        };

        super(expandedOptions);
    }
}

export class Row extends FieldBase<unknown> {
    constructor(options: FieldOptions<unknown>) {
        const expandedOptions = {
            ...options,
            controlType: 'row',
            type: 'row',
            className: options.className,
        };

        super(expandedOptions);
    }
}

export class Label<T> extends FieldBase<T> {
    constructor(options: FieldOptions<T>) {
        const expandedOptions = {
            ...options,
            controlType: 'label',
            type: 'label',
        };

        super(expandedOptions);
    }
}
export class InputField<T> extends FieldBase<T> {
    constructor(options: FieldOptions<T>) {
        const expandedOptions = {
            ...options,
            controlType: 'input',
            type: 'input',
            suffix: options.suffix
        };

        super(expandedOptions);
    }
}

export class InputWithSuffixField<T> extends FieldBase<T> {
    min?: number;

    constructor(options: FieldOptions<T> & { min?: number }) {
        const expandedOptions = {
            ...options,
            controlType: 'inputWithSuffix',
            type: 'input',
        };

        super(expandedOptions);
        this.min = options.min;
    }
}

export class LookupField extends FieldBase<string> {
    constructor(options: FieldOptions<string>) {
        const expandedLookupValues = options.options || [];

        if (
            options.value &&
            options.value !== '' &&
            !expandedLookupValues
                .map((option) => option.value)
                .includes(options.value || '')
        ) {
            expandedLookupValues.push({
                value: options.value || '',
                viewValue: options.value || '',
            });
        }

        const expandedOptions = {
            ...options,
            controlType: 'lookup',
            type: 'lookup',
            options: expandedLookupValues.length ? expandedLookupValues : [],
        };

        super(expandedOptions);
    }
}

export class AutoCompleteField<T = string> extends FieldBase<T> {
    constructor(options: FieldOptions<T>) {
        const expandedOptions = {
            ...options,
            controlType: 'autocomplete',
            type: 'autocomplete',
            url: options.url || ''
        };

        super(expandedOptions);
    }
}

export class MultiselectAutoCompleteField extends FieldBase<FieldOption[]> {
    constructor(options: FieldOptions<FieldOption[]>) {
        const expandedOptions = {
            ...options,
            controlType: 'autocomplete',
            type: 'autocomplete',
            url: options.url || ''
        };

        super(expandedOptions);
    }
}

export class Checkbox extends FieldBase<unknown> {
    constructor(options: FieldOptions<unknown>) {
        const expandedOptions = {
            ...options,
            controlType: 'checkbox',
            type: 'checkbox',
        };

        super(expandedOptions);
    }
}

export class Datepicker extends FieldBase<Date> {
    constructor(options: FieldOptions<Date>) {
        const expandedOptions = {
            ...options,
            controlType: 'datepicker',
            type: 'datepicker',
        };

        super(expandedOptions);
    }
}

export class DateRangePicker extends FieldBase<DateRangeType> {
    constructor(options: FieldOptions<DateRangeType>) {
        const expandedOptions = {
            ...options,
            controlType: 'dateRangePicker',
            type: 'dateRangePicker',

        };

        super(expandedOptions);
    }
}

export class Fab extends FieldBase<string> {
    constructor(options: FieldOptions<string>) {
        const expandedOptions = {
            ...options,
            controlType: 'fab',
        };

        super(expandedOptions);
    }
}

export class ChipSet extends FieldBase<unknown[]> {
    constructor(options: FieldOptions<unknown[]>) {
        const expandedOptions = {
            ...options,
            controlType: 'chipSet',
            type: 'chipSet',
        };

        super(expandedOptions);
    }
}
